import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger } from '@mono/ui-components';
import { Award, Calculator, ChevronDown, LayoutDashboard, PenTool, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useFeatureFlag, useFeatureFlagsReady } from '../../../../contexts/featureFlagContext';
import { WORKSPACE_LIST } from './workspaceConfig';
const ICON_MAP = {
    LayoutDashboard,
    PenTool,
    Calculator,
    ShieldCheck,
    Award
};
/**
 * WorkspaceSwitcher — dropdown in the compliance hub header that lets
 * users switch between role-based lenses.
 *
 * Not a permission gate — anyone can switch. It just changes which tabs
 * are visible and which tab is active by default.
 */ export function WorkspaceSwitcher({ current, onSelect }) {
    const flagsReady = useFeatureFlagsReady();
    const specraRedesignEnabled = useFeatureFlag('specra_redesign_dashboard');
    const specraRedesign = flagsReady && specraRedesignEnabled;
    if (specraRedesign) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "mb-1.5 px-1 text-[13px] font-medium text-foreground",
                    children: current.label
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex w-full items-center justify-around",
                    children: WORKSPACE_LIST.map((ws)=>{
                        const Icon = ICON_MAP[ws.icon] || LayoutDashboard;
                        const isActive = ws.id === current.id;
                        return /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: ()=>onSelect(ws.id),
                            title: ws.label,
                            "aria-label": ws.label,
                            "aria-pressed": isActive,
                            className: `flex items-center justify-center rounded-md p-1.5 transition-colors ${isActive ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`,
                            children: /*#__PURE__*/ _jsx(Icon, {
                                className: "h-4 w-4 flex-shrink-0"
                            })
                        }, ws.id);
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(WorkspaceSwitcherDropdown, {
        current: current,
        onSelect: onSelect
    });
}
/** Legacy dropdown version — used when redesign flag is off. */ function WorkspaceSwitcherDropdown({ current, onSelect }) {
    const [open, setOpen] = useState(false);
    const CurrentIcon = ICON_MAP[current.icon] || LayoutDashboard;
    return /*#__PURE__*/ _jsxs(Popover, {
        open: open,
        onOpenChange: setOpen,
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsxs("button", {
                    type: "button",
                    className: "group flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm transition-colors hover:border-slate-300 hover:bg-slate-50",
                    children: [
                        /*#__PURE__*/ _jsx(CurrentIcon, {
                            className: "h-4 w-4 text-slate-500"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium text-slate-700",
                            children: current.label
                        }),
                        /*#__PURE__*/ _jsx(ChevronDown, {
                            className: "h-3.5 w-3.5 text-slate-400 transition-transform group-data-[state=open]:rotate-180"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(PopoverContent, {
                className: "w-72 p-1.5",
                align: "start",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "space-y-0.5",
                    children: WORKSPACE_LIST.map((ws)=>{
                        const Icon = ICON_MAP[ws.icon] || LayoutDashboard;
                        const isActive = ws.id === current.id;
                        return /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            onClick: ()=>{
                                onSelect(ws.id);
                                setOpen(false);
                            },
                            className: `flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`,
                            children: [
                                /*#__PURE__*/ _jsx(Icon, {
                                    className: `mt-0.5 h-4 w-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400'}`
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "min-w-0 flex-1",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: `text-sm font-medium ${isActive ? 'text-white' : 'text-slate-800'}`,
                                            children: ws.label
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: `mt-0.5 text-[11px] leading-tight ${isActive ? 'text-slate-300' : 'text-slate-500'}`,
                                            children: ws.description
                                        })
                                    ]
                                })
                            ]
                        }, ws.id);
                    })
                })
            })
        ]
    });
}
